import {
  IBusinessColorConfig,
  IBusinessConfigs,
} from '../_Common/Const/BusinessConfigs';
import { initConst } from '../../../Utils/utils';
import displayStatistikStatus from './Utils/DisplayStatistikStatus';
import DisplayAktivitetstyp from './Utils/DisplayAktivitetstyp';
import { isImageExtension } from '../../../Components/Documents/Documents';
import DokumentDownloadIkon from '../../../Components/DokumentDownloadIkon';
import DokumentViewIkon from '../../../Components/DokumentViewIkon';
import { TEDBOAktivitetTypEnum } from '../../../globalQueryTypes';
import FakturaDokumentAddComp from '../Leverantor/ObjektVyer/FakturaDok/Components/FakturaDokumentAddComp';

// Färger för färgkarta i rutin Aktiviteter och Bokning
const createActivityColorStyles = (
  dataPrefix: string = ''
): IBusinessColorConfig[] => {
  const prefix = dataPrefix ? `${dataPrefix}.` : '';

  return [
    {
      caption: 'Övrig aktivitet',
      color: 'ovrigColor',
      condition: (data: any) => data._typ === TEDBOAktivitetTypEnum.AKTOVRIGT,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Gudstjänst',
      color: 'gudstColor',
      condition: (data: any) => data._typ === TEDBOAktivitetTypEnum.GUDSTJANST,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Begravning',
      color: 'begrColor',
      condition: (data: any) => data._typ === TEDBOAktivitetTypEnum.BEGRAVNING,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Gravsättning',
      color: 'gravsColor',
      condition: (data: any) =>
        data._typ === TEDBOAktivitetTypEnum.GRAVSATTNING,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Dop',
      color: 'dopColor',
      condition: (data: any) => data._typ === TEDBOAktivitetTypEnum.DOP,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Vigsel',
      color: 'vigselColor',
      condition: (data: any) => data._typ === TEDBOAktivitetTypEnum.VIGSEL,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Konfirmation',
      color: 'konfColor',
      condition: (data: any) =>
        data._typ === TEDBOAktivitetTypEnum.KONFIRMATION,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Lokaluthyrning',
      color: 'lokalColor',
      condition: (data: any) =>
        data._typ === TEDBOAktivitetTypEnum.LOKALUTHYRNING,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Snabbokning',
      color: 'snabbColor',
      condition: (data: any) => data._typ === TEDBOAktivitetTypEnum.SNABBOKNING,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Bokad i Outlook',
      color: 'outlookColor',
      condition: (data: any) => data._typ === TEDBOAktivitetTypEnum.OUTLOOK,
      dataDependencies: [`${prefix}_typ`],
    },
    {
      caption: 'Preliminärbokad',
      greyBox: true,
      condition: (data: any) => data.PRELBOKAD,
      dataDependencies: [`${prefix}PRELBOKAD`],
    },
  ];
};

const BOBusinessConfig = initConst<IBusinessConfigs>(() => ({
  BOAktivitet: {
    defaultColumns: [
      { attributeDef: 'TITEL', caption: 'Titel', header: true },
      { attributeDef: '_typ', caption: 'Aktivitetstyp' },
      { attributeDef: 'FRANDATUM', caption: 'Fråndatum' },
      { attributeDef: 'FRANTID', caption: 'Från kl' },
      { attributeDef: 'Resurs1.RESURS_NAMN', caption: 'Plats' },
      { attributeDef: 'StatistikTyp', caption: 'Statistiktyp' },
      { attributeDef: 'BOKNINGSNUMMER', caption: 'BokNr' },
      { attributeDef: 'StatistikStatus', caption: 'Status' },
    ],
    defaultSort: [
      { attributeName: 'FRANDATUM', order: 'asc' },
      { attributeName: 'FRANTID', order: 'asc' },
      { attributeName: 'Resurs1.RESURS_NAMN', order: 'asc' },
    ],
    attributeWithColors: '_typ',
    colors: {
      styles: createActivityColorStyles(),
    },

    attributes: {
      _typ: {
        displayComp: DisplayAktivitetstyp,
        tableMinWidth: 160,
      },
      StatistikStatus: {
        displayComp: displayStatistikStatus,
        size: 'small',
        tableDefaultWidth: 75,
      },
      TotalaBesokare: { tableDefaultWidth: { xs: 75, xl: 150 } },
      Titel2: { tableDefaultWidth: { xl: 450 } },
      DatumOchTid: { tableDefaultWidth: { xl: 200 } },
    },
    dataDependencies: [
      'PRELBOKAD',
      '_typ',
      'DigitalAktivitet',
      'AldersindeladAktivitet',
      'DatumOchTid',
      'Bokningsgrupp.BENAMNING',
      'BORTTAGEN',
      'IDString',
      'BOKNINGSSIGNATUR',
    ],
  },
  BOPlanering: {
    defaultColumns: [
      { attributeDef: 'FRANDATUM', caption: 'Fråndatum' },
      { attributeDef: 'FRANTID', caption: 'Fråntid' },
      { attributeDef: 'TITEL', caption: 'Titel' },
      { attributeDef: 'TILLTID', caption: 'Tilltid' },
    ],

    defaultSort: [
      { attributeName: 'FRANDATUM', order: 'asc' },
      { attributeName: 'FRANTID', order: 'asc' },
    ],
  },
  BOAktivitetDok: {
    defaultColumns: [
      { attributeDef: 'BESKRIVNING', caption: 'Beskrivning', header: true },
    ],
    listIcons: [
      {
        componentFn: config => {
          const extension: string = config.data?.EXTENSION?.toLowerCase() ?? '';
          const hidden = !isImageExtension(extension) && extension !== 'pdf';

          return (
            <DokumentViewIkon
              documents={[
                {
                  BINARDATA: config?.data?.BINARDATA,
                  BESKRIVNING: config?.data?.BESKRIVNING,
                  EXTENSION: config?.data?.EXTENSION,
                },
              ]}
              key="AktivitetDokumentViewIkon"
              hidden={hidden}
            />
          );
        },
        dataDependencies: ['BINARDATA', 'BESKRIVNING', 'EXTENSION'],
      },
      {
        componentFn: config => (
          <DokumentDownloadIkon
            key="AktivitetDokumentDownloadIkon"
            blobId={config.data?.BINARDATA}
            extension={config.data?.EXTENSION}
            fileName={config.data?.BESKRIVNING}
          />
        ),
        dataDependencies: ['BINARDATA'],
      },
    ],
    addComponent: FakturaDokumentAddComp,
  },
  // New config for the Kalender routine.
  // Rooted on BOBokningsrubrik (TEDBOBokningsrubrikSaml) instead of BOBokning.
  // One row per resource for the full aktivitet span — no per-day expansion in SQL.
  BOBokningsrubrik: {
    defaultColumns: [
      { attributeDef: 'Aktivitet.BOKNINGSNUMMER', header: true },
      { attributeDef: 'FranDatumTid' },
      { attributeDef: 'TillDatumTid' },
      { attributeDef: 'RESURS_ID' },
      { attributeDef: 'RESURS_NAMN' },
      { attributeDef: 'Resursrubrik' },
    ],
    colors: {
      styles: createActivityColorStyles('Aktivitet'),
    },
    attributes: {
      RESURS_NAMN: { tableDefaultWidth: { xs: 200, xl: 300 } },
    },
    dataDependencies: [
      'FRANDATUM',
      'FRANTID',
      'TILLDATUM',
      'TILLTID',
      'RESURS_NAMN',
      'RESURS_ID',
      'Resurs.BO_RESURS_ID',
      'Aktivitet._typ',
      'Aktivitet.TITEL',
      'Aktivitet.FRANDATUM',
      'Aktivitet.FRANTID',
      'Aktivitet.TILLDATUM',
      'Aktivitet.TILLTID',
      'Aktivitet.BOKNINGSNUMMER',
      'Aktivitet.PRELBOKAD',
      'Aktivitet.BORTTAGEN',
      'Aktivitet.BOKNINGSSIGNATUR',
      //'Aktivitet.Bokningsbar', OBS farligt blir långsamt
      'Aktivitet.Resurs1.RESURS_NAMN',
      // Used by getActivityIcon:
      'Aktivitet.BEVAKNING',
      'Aktivitet.KREMERING',
      'Aktivitet.SKK_SPRIDNINGSTYP_EXTERN',
      'Aktivitet.Avlidna.AKT_MED_URNA',
      'Aktivitet.Dokument.BINARDATA',
      'Aktivitet.REPAKTNR',
      'Aktivitet.Huvudakt', // Bokningsnummer knuten (huvud)Gudstjänst visas på Dop, Vigsel, Begravning.
      'Aktivitet.AktBokRubrik.RESURS_NAMN',
    ],
  },
  BOFaktOmg: {
    defaultColumns: [
      { attributeDef: 'Aktivitet.BOKNINGSNUMMER', caption: 'Bokningsnr' },
      { attributeDef: 'Aktivitet.TITEL', caption: 'Titel' },
      { attributeDef: 'Aktivitet.TIDSTEXT', caption: 'Tid' },
      { attributeDef: 'BENAMNING', caption: 'Underlag' },
      { attributeDef: 'AktuelltTotBelopp2', caption: 'Belopp' },
      { attributeDef: 'FORRATTNINGSTYP', caption: 'Förrättningstyp' },
      { attributeDef: 'Kund.KUNDNR_KONTONR', caption: 'Kundnr' },
      { attributeDef: 'Kund.Namn', caption: 'Kundnamn' },
      { attributeDef: 'SKAPAD_DATUM', caption: 'Datum' },
      { attributeDef: 'SKAPAD_SIGNATUR', caption: 'Signatur' },
    ],
  },
  BOResurs: {
    defaultColumns: [
      { attributeDef: 'BO_RESURS_ID', caption: 'ResursID' },
      { attributeDef: 'BO_RESURS_NAMN', caption: 'Resursnamn' },
      { attributeDef: 'ResRestyp.BO_RESURSTYP_NAMN', caption: 'Resurstyp' },
      { attributeDef: 'Anvandare.SIGNATURE', caption: 'Användare' },
      { attributeDef: 'RESURSNAMN_EXTERN', caption: 'Externt resursnamn' },
      { attributeDef: 'BESKRIVNING', caption: 'Beskrivning' },
      { attributeDef: 'PRIO_ORDNING', caption: 'Prioriteringsordning' },
    ],
  },
  BOGruppOP: {
    defaultColumns: [
      { attributeDef: 'BENAMNING', caption: 'Benämning' },
      { attributeDef: 'KATEGORI', caption: 'Kategori' },
      { attributeDef: 'Antal2', caption: 'Antal' },
      { attributeDef: 'BESKRIVNING', caption: 'Beskrivning' },
    ],
  },
  BOGruppmedlemOP: {
    defaultColumns: [
      { attributeDef: 'PersNr', caption: 'Födelsedatum' },
      { attributeDef: 'sqlNamn', caption: 'Namn' },
      { attributeDef: 'sqlTelefon', caption: 'Telefon' },
      { attributeDef: 'sqlSMTP', caption: 'E-post' },
      { attributeDef: 'sqlAdress', caption: 'Adress' },
      { attributeDef: 'sqlMobiltelefon', caption: 'Mobiltelefon' },
      { attributeDef: 'sqlArbetstelefon', caption: 'Arbetstelefon' },
      { attributeDef: 'Gruppbenamning', caption: 'Grupp' },
      { attributeDef: 'sqlSMTP2', caption: 'E-post (2)' },
    ],
  },
  BOSammankomst: {
    defaultColumns: [
      { attributeDef: 'GruppNamn', caption: 'Grupp av personer' },
      { attributeDef: 'TidPunkt', caption: 'Tid' },
      { attributeDef: 'BENAMNING', caption: 'Titel' },
      { attributeDef: 'AktivitetBokNr', caption: 'Aktivitet' },
    ],
  },
  BOAktivitetstyp: {
    defaultColumns: [
      { attributeDef: 'NAMN', caption: 'Aktivitetstyp' },
      { attributeDef: 'AKTIV', caption: 'Aktiv' },
      {
        attributeDef: 'AkttypNrSerie.BENAMNING',
        caption: 'Bokningsnummerserie',
      },
      { attributeDef: 'Prefix', caption: '' },
      { attributeDef: 'Nummer', caption: 'Nästa nr' },
    ],
  },
  BOBegravningstyp: {
    defaultColumns: [
      { attributeDef: 'KOD', caption: 'Kod' },
      { attributeDef: 'BENAMNING', caption: 'Benämning' },
      { attributeDef: 'CLEARINGSTYP', caption: 'Typ av clearing' },
    ],
  },
  BOVerksamhetstyper: {
    defaultColumns: [
      { attributeDef: 'KOD', caption: 'Kod', header: true },
      { attributeDef: 'BENAMNING', caption: 'Benämning', header: true },
      { attributeDef: 'ANNONSERAS', caption: 'Annonseras' },
      { attributeDef: 'STATISTIKUPPGIFT', caption: 'Statistik' },
      { attributeDef: 'CATEGORY' },
    ],
  },
  BOAktivitetResRubrik: {
    defaultColumns: [
      { attributeDef: 'Resursrubrik', caption: 'Rubrik', header: true },
      { attributeDef: 'POSNR', caption: 'Position' },
    ],
  },
  BOGudstjansttyp: {
    defaultColumns: [
      { attributeDef: 'FORK', caption: 'Förkortning' },
      { attributeDef: 'NAMN', caption: 'Benämning' },
      { attributeDef: 'HUVUDGUDSTJANST', caption: 'Huvudgudstjänst' },
      { attributeDef: 'INGARPREDIKOTUR', caption: 'Ingår predikotur' },
    ],
  },
  BOPlaneringskategori: {
    defaultColumns: [
      { attributeDef: 'BENAMNING', caption: 'Benämning' },
      { attributeDef: 'TYP', caption: 'Typ' },
      { attributeDef: 'FRANTID', caption: 'Fråntid' },
      { attributeDef: 'TILLTID', caption: 'Tilltid' },
    ],
  },
  BOResursgrupp: {
    defaultColumns: [{ attributeDef: 'BENAMNING', caption: 'Benämning' }],
  },
  BOBisattning: {
    defaultColumns: [
      { attributeDef: 'JOURNALNR', caption: 'Nr', header: true },
      { attributeDef: 'Avlidna.Person.OrgNrPersNr', caption: 'Persnr' },
      { attributeDef: 'Avlidna.Person.FORNAMN', caption: 'Förnamn' },
      { attributeDef: 'Avlidna.Person.EFTERNAMN', caption: 'Efternamn' },
      { attributeDef: 'Avlidna.BegrDatum', caption: 'Begravningsdatum' },
      { attributeDef: 'HandelseStatus', caption: 'Status' },
      { attributeDef: 'BegrByraNamn', caption: 'Begravningsbyrå' },
      { attributeDef: 'BisattnTypBen', caption: 'Bisättningstyp' },
      { attributeDef: 'HandelseDatum', caption: 'Händelsedatum' },
      { attributeDef: 'AntalDagar', caption: 'Antal Dagar i Lokalen' },
      { attributeDef: 'HandelsePlacering', caption: 'Placering' },
    ],
    dataDependencies: ['BORTTAGEN'],
  },
  BOKategori: {
    defaultColumns: [
      { attributeDef: 'KOD', caption: 'Kod' },
      { attributeDef: 'BENAMNING', caption: 'Benämning' },
    ],
  },
}));

export default BOBusinessConfig;
